import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Package, 
  MoreHorizontal, 
  Upload, 
  Trash2, 
  Edit,
  Loader2,
  FolderTree,
  FileText,
  Link,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Tag,
  Calendar,
  User,
  Save,
  X,
  Download,
  Maximize2,
  File,
  Image,
  FileSpreadsheet,
  Presentation
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get file icon based on file type
const getFileIcon = (fileName) => {
  if (!fileName) return <File className="w-5 h-5 text-muted-foreground" />;
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
    return <Image className="w-5 h-5 text-green-500" />;
  }
  if (['pdf'].includes(ext)) {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (['doc', 'docx'].includes(ext)) {
    return <FileText className="w-5 h-5 text-blue-500" />;
  }
  if (['xls', 'xlsx'].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
  }
  if (['ppt', 'pptx'].includes(ext)) {
    return <Presentation className="w-5 h-5 text-orange-500" />;
  }
  return <File className="w-5 h-5 text-muted-foreground" />;
};

// Helper function for status badge
const getStatusBadge = (status) => {
  const statusConfig = {
    draft: { variant: 'secondary', label: 'Draft' },
    pending: { variant: 'outline', label: 'Pending Review' },
    approved: { variant: 'default', label: 'Approved' },
    rejected: { variant: 'destructive', label: 'Rejected' },
    archived: { variant: 'secondary', label: 'Archived' }
  };
  const config = statusConfig[status] || { variant: 'secondary', label: status };
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const Assets = () => {
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allAssets, setAllAssets] = useState([]); // For parent selection
  const [metadataFields, setMetadataFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDocumentDetailModal, setShowDocumentDetailModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [expandedAssets, setExpandedAssets] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_ids: [],
    parent_id: '',
    tags: '',
    metadata: {}
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Asset detail modal states
  const [isEditingAsset, setIsEditingAsset] = useState(false);
  const [savingAssetEdit, setSavingAssetEdit] = useState(false);
  const [editAssetFormData, setEditAssetFormData] = useState({
    name: '',
    description: '',
    project_ids: [],
    parent_id: '',
    tags: []
  });
  
  // Document detail modal states
  const [isEditingDocument, setIsEditingDocument] = useState(false);
  const [savingDocEdit, setSavingDocEdit] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [revisionCodes, setRevisionCodes] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewInfo, setPreviewInfo] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [editDocFormData, setEditDocFormData] = useState({
    name: '',
    description: '',
    document_number: '',
    document_type_id: '',
    revision_code_id: '',
    project_id: '',
    asset_ids: [],
    tags: []
  });

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data.assets);
      setAllAssets(response.data.assets);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Failed to load projects');
    }
  }, [token]);

  const fetchMetadataFields = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/metadata-fields?entity_type=asset`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetadataFields(response.data.fields);
    } catch (error) {
      console.error('Failed to load metadata fields');
    }
  }, [token]);

  const fetchDocumentTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/document-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentTypes(response.data.document_types || []);
    } catch (error) {
      console.error('Failed to load document types');
    }
  }, [token]);

  const fetchRevisionCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/revision-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevisionCodes(response.data.revision_codes || []);
    } catch (error) {
      console.error('Failed to load revision codes');
    }
  }, [token]);

  const fetchAssetDetail = useCallback(async (assetId) => {
    try {
      const response = await axios.get(`${API_URL}/api/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAsset(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load asset details');
    }
  }, [token]);

  const fetchDocumentDetail = useCallback(async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setShowDocumentDetailModal(true);
      
      // Load preview info inline
      setPreviewLoading(true);
      setPreviewBlobUrl(null);
      setPreviewInfo(null);
      
      try {
        const previewResponse = await axios.get(`${API_URL}/api/documents/${docId}/preview-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPreviewInfo(previewResponse.data);
        
        // Load blob for images/PDFs only if file exists
        if (previewResponse.data.has_file && ['image', 'pdf'].includes(previewResponse.data.preview_type)) {
          try {
            const blobResponse = await axios.get(`${API_URL}/api/documents/${docId}/download`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob'
            });
            if (blobResponse.data && blobResponse.data.size > 0) {
              const url = URL.createObjectURL(blobResponse.data);
              setPreviewBlobUrl(url);
            }
          } catch (blobError) {
            // File might not exist on disk, this is okay
            console.log('File not available for preview');
          }
        }
      } catch (previewError) {
        // Preview info endpoint failed, set to null
        console.log('Preview info not available');
        setPreviewInfo({ has_file: false });
      } finally {
        setPreviewLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load document details');
    }
  }, [token]);

  // Document editing functions
  const startEditingDocument = () => {
    if (!selectedDocument) return;
    setEditDocFormData({
      name: selectedDocument.name || '',
      description: selectedDocument.description || '',
      document_number: selectedDocument.document_number || '',
      document_type_id: selectedDocument.document_type_id || '',
      revision_code_id: selectedDocument.revision_code_id || '',
      project_id: selectedDocument.project_id || '',
      asset_ids: selectedDocument.asset_ids || [],
      tags: selectedDocument.tags || []
    });
    setIsEditingDocument(true);
  };

  const cancelEditingDocument = () => {
    setIsEditingDocument(false);
    setEditDocFormData({
      name: '',
      description: '',
      document_number: '',
      document_type_id: '',
      revision_code_id: '',
      project_id: '',
      asset_ids: [],
      tags: []
    });
  };

  const handleSaveDocumentEdit = async () => {
    if (!selectedDocument) return;
    setSavingDocEdit(true);
    
    try {
      await axios.put(`${API_URL}/api/documents/${selectedDocument.id}`, {
        name: editDocFormData.name,
        description: editDocFormData.description,
        document_number: editDocFormData.document_number,
        document_type_id: editDocFormData.document_type_id || null,
        revision_code_id: editDocFormData.revision_code_id || null,
        project_id: editDocFormData.project_id || null,
        asset_ids: editDocFormData.asset_ids,
        tags: editDocFormData.tags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Document updated successfully');
      
      // Refresh document detail
      const response = await axios.get(`${API_URL}/api/documents/${selectedDocument.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setIsEditingDocument(false);
      
      // Refresh assets to update linked documents
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setSavingDocEdit(false);
    }
  };

  const toggleEditDocAssetSelection = (assetId) => {
    setEditDocFormData(prev => ({
      ...prev,
      asset_ids: prev.asset_ids.includes(assetId)
        ? prev.asset_ids.filter(id => id !== assetId)
        : [...prev.asset_ids, assetId]
    }));
  };

  const handleEditDocTagKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim();
      if (!editDocFormData.tags.includes(newTag)) {
        setEditDocFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      e.target.value = '';
    }
  };

  const removeEditDocTag = (tagToRemove) => {
    setEditDocFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || `${doc.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const openFullPreview = (doc) => {
    if (previewInfo?.preview_type === 'office') {
      // Open in Office Online
      const officeUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(API_URL + '/api/documents/' + doc.id + '/download')}`;
      window.open(officeUrl, '_blank');
    } else if (previewBlobUrl) {
      window.open(previewBlobUrl, '_blank');
    }
  };

  // Asset editing functions
  const startEditingAsset = () => {
    if (!selectedAsset) return;
    setEditAssetFormData({
      name: selectedAsset.name || '',
      description: selectedAsset.description || '',
      project_ids: selectedAsset.project_ids || [],
      parent_id: selectedAsset.parent_id || '',
      tags: selectedAsset.tags || []
    });
    setIsEditingAsset(true);
  };

  const cancelEditingAsset = () => {
    setIsEditingAsset(false);
    setEditAssetFormData({
      name: '',
      description: '',
      project_ids: [],
      parent_id: '',
      tags: []
    });
  };

  const handleSaveAssetEdit = async () => {
    if (!selectedAsset) return;
    setSavingAssetEdit(true);
    
    try {
      await axios.put(`${API_URL}/api/assets/${selectedAsset.id}`, {
        name: editAssetFormData.name,
        description: editAssetFormData.description,
        project_ids: editAssetFormData.project_ids,
        parent_id: editAssetFormData.parent_id || null,
        tags: editAssetFormData.tags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Asset updated successfully');
      
      // Refresh asset detail
      const response = await axios.get(`${API_URL}/api/assets/${selectedAsset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAsset(response.data);
      setIsEditingAsset(false);
      
      // Refresh assets list
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update asset');
    } finally {
      setSavingAssetEdit(false);
    }
  };

  const toggleEditAssetProjectSelection = (projectId) => {
    setEditAssetFormData(prev => ({
      ...prev,
      project_ids: prev.project_ids.includes(projectId)
        ? prev.project_ids.filter(id => id !== projectId)
        : [...prev.project_ids, projectId]
    }));
  };

  const handleEditAssetTagKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newTag = e.target.value.trim();
      if (!editAssetFormData.tags.includes(newTag)) {
        setEditAssetFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      e.target.value = '';
    }
  };

  const removeEditAssetTag = (tagToRemove) => {
    setEditAssetFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  useEffect(() => {
    fetchAssets();
    fetchProjects();
    fetchMetadataFields();
    fetchDocumentTypes();
    fetchRevisionCodes();
  }, [fetchAssets, fetchProjects, fetchMetadataFields, fetchDocumentTypes, fetchRevisionCodes]);

  // Cleanup blob URL on unmount or when modal closes
  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/assets`, {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        parent_id: formData.parent_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create asset');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedAsset) return;

    try {
      await axios.put(`${API_URL}/api/assets/${selectedAsset.id}`, {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        parent_id: formData.parent_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update asset');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedAsset) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      await axios.post(`${API_URL}/api/assets/${selectedAsset.id}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('File uploaded successfully');
      setShowUploadModal(false);
      setUploadFile(null);
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset? This will also unlink it from any children.')) return;

    try {
      await axios.delete(`${API_URL}/api/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset deleted');
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      project_ids: [],
      parent_id: '',
      tags: '',
      metadata: {}
    });
    setSelectedAsset(null);
  };

  const openEditModal = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      name: asset.name,
      description: asset.description || '',
      project_ids: asset.project_ids || [],
      parent_id: asset.parent_id || '',
      tags: asset.tags?.join(', ') || '',
      metadata: asset.metadata || {}
    });
    setShowEditModal(true);
  };

  const toggleProjectSelection = (projectId) => {
    setFormData(prev => ({
      ...prev,
      project_ids: prev.project_ids.includes(projectId)
        ? prev.project_ids.filter(id => id !== projectId)
        : [...prev.project_ids, projectId]
    }));
  };

  // Toggle asset expansion (show/hide children and documents)
  const toggleAssetExpanded = (assetId) => {
    setExpandedAssets(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
  };

  // Filter assets
  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get root assets (no parent) sorted by name
  const rootAssets = filteredAssets
    .filter(a => !a.parent_id)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Get children of a specific parent
  const getChildren = (parentId) => filteredAssets
    .filter(a => a.parent_id === parentId)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Render asset row recursively
  const renderAssetRow = (asset, level = 0) => {
    const hasChildren = asset.children_count > 0;
    const isExpanded = expandedAssets.has(asset.id);
    const children = getChildren(asset.id);
    const linkedDocs = asset.linked_documents || [];
    const indentPadding = level * 24;

    return (
      <React.Fragment key={asset.id}>
        {/* Asset Row */}
        <div 
          className={`flex items-center gap-2 p-3 border-b border-border hover:bg-muted/50 transition-colors`}
          data-testid={`asset-row-${asset.id}`}
        >
          {/* Indent + Expand button */}
          <div style={{ paddingLeft: `${indentPadding}px` }} className="flex items-center">
            {(hasChildren || linkedDocs.length > 0) ? (
              <button 
                onClick={() => toggleAssetExpanded(asset.id)}
                className="p-1 hover:bg-muted rounded mr-1"
                data-testid={`expand-asset-${asset.id}`}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
            ) : (
              <span className="w-6 mr-1" />
            )}
          </div>

          {/* Asset Icon */}
          <div className="p-2 rounded-lg bg-muted">
            <Package className="w-4 h-4 text-muted-foreground" />
          </div>

          {/* Asset Name & Description */}
          <div className="flex-1 min-w-0">
            <button 
              className="font-medium text-left hover:text-primary hover:underline transition-colors cursor-pointer"
              onClick={() => fetchAssetDetail(asset.id)}
              data-testid={`asset-name-${asset.id}`}
            >
              {asset.name}
            </button>
            {asset.description && (
              <p className="text-xs text-muted-foreground truncate max-w-md">
                {asset.description}
              </p>
            )}
          </div>

          {/* Stats badges */}
          <div className="flex items-center gap-2">
            {hasChildren && (
              <Badge variant="secondary" className="text-xs" title="Child assets">
                <FolderTree className="w-3 h-3 mr-1" />
                {asset.children_count}
              </Badge>
            )}
            {linkedDocs.length > 0 && (
              <Badge variant="outline" className="text-xs" title="Linked documents">
                <FileText className="w-3 h-3 mr-1" />
                {linkedDocs.length}
              </Badge>
            )}
          </div>

          {/* Projects */}
          <div className="flex flex-wrap gap-1 w-40">
            {asset.project_names?.length > 0 ? (
              <>
                {asset.project_names.slice(0, 1).map((name, index) => (
                  <Badge key={index} variant="outline" className="text-xs truncate max-w-[120px]">
                    {name}
                  </Badge>
                ))}
                {asset.project_names.length > 1 && (
                  <Badge variant="outline" className="text-xs">
                    +{asset.project_names.length - 1}
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-muted-foreground text-xs">—</span>
            )}
          </div>

          {/* Created date */}
          <span className="text-sm text-muted-foreground w-28">
            {format(new Date(asset.created_at), 'MMM d, yyyy')}
          </span>

          {/* Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`asset-actions-${asset.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setSelectedAsset(asset); setShowUploadModal(true); }}>
                <Upload className="w-4 h-4 mr-2" />
                Upload File
              </DropdownMenuItem>
              {hasPermission('edit_assets') && (
                <DropdownMenuItem onClick={() => openEditModal(asset)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {hasPermission('manage_assets') && (
                <DropdownMenuItem 
                  onClick={() => handleDelete(asset.id)}
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Expanded content: Documents + Child assets */}
        {isExpanded && (
          <>
            {/* Linked Documents */}
            {linkedDocs.length > 0 && (
              <div className="bg-muted/30">
                {linkedDocs.map(doc => (
                  <div 
                    key={doc.id}
                    className="flex items-center gap-2 p-2 pl-4 border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer"
                    style={{ paddingLeft: `${indentPadding + 48}px` }}
                    onClick={() => fetchDocumentDetail(doc.id)}
                    data-testid={`doc-link-${doc.id}`}
                  >
                    <FileText className="w-4 h-4 text-blue-500" />
                    {doc.document_number && (
                      <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {doc.document_number}
                      </span>
                    )}
                    <span className="text-sm flex-1 hover:text-primary hover:underline">
                      {doc.name}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {doc.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {/* Child Assets (recursive) */}
            {children.map(child => renderAssetRow(child, level + 1))}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Assets</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your digital assets with hierarchy and project links
          </p>
        </div>
        {hasPermission('create_assets') && (
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-asset-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Asset
          </Button>
        )}
      </div>

      <div className="p-8">
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-assets"
            />
          </div>
        </div>

        {/* Hierarchical Asset Tree */}
        <Card>
          <CardContent className="p-0">
            {/* Header */}
            <div className="flex items-center gap-2 p-3 border-b border-border bg-muted/50 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <div className="w-8" /> {/* Expand spacer */}
              <div className="w-10" /> {/* Icon spacer */}
              <div className="flex-1">Asset Name</div>
              <div className="w-24 text-center">Info</div>
              <div className="w-40">Projects</div>
              <div className="w-28">Created</div>
              <div className="w-10" /> {/* Actions spacer */}
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : rootAssets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No assets found</p>
              </div>
            ) : (
              <div>
                {rootAssets.map(asset => renderAssetRow(asset, 0))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Asset Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Asset Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="asset-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="asset-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent">Parent Asset</Label>
              <Select
                value={formData.parent_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="asset-parent-select">
                  <SelectValue placeholder="No parent (root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (root)</SelectItem>
                  {allAssets.map((asset) => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Multiple Projects Selection */}
            <div className="space-y-2">
              <Label>Link to Projects</Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                {projects.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No projects available</p>
                ) : (
                  projects.map((project) => (
                    <div key={project.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`project-${project.id}`}
                        checked={formData.project_ids.includes(project.id)}
                        onCheckedChange={() => toggleProjectSelection(project.id)}
                      />
                      <Label htmlFor={`project-${project.id}`} className="text-sm cursor-pointer">
                        {project.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {formData.project_ids.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.project_ids.length} project(s) selected
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="logo, branding, final"
                data-testid="asset-tags-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="create-asset-submit">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Asset Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Asset Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-parent">Parent Asset</Label>
              <Select
                value={formData.parent_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No parent (root)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (root)</SelectItem>
                  {allAssets.filter(a => a.id !== selectedAsset?.id).map((asset) => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Multiple Projects Selection */}
            <div className="space-y-2">
              <Label>Link to Projects</Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                {projects.map((project) => (
                  <div key={project.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`edit-project-${project.id}`}
                      checked={formData.project_ids.includes(project.id)}
                      onCheckedChange={() => toggleProjectSelection(project.id)}
                    />
                    <Label htmlFor={`edit-project-${project.id}`} className="text-sm cursor-pointer">
                      {project.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-tags">Tags (comma-separated)</Label>
              <Input
                id="edit-tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(open) => {
        if (!open) {
          setIsEditingAsset(false);
          cancelEditingAsset();
        }
        setShowDetailModal(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedAsset && (
            <>
              <DialogHeader className="flex flex-row items-start justify-between">
                <div className="flex-1">
                  {isEditingAsset ? (
                    <Input
                      value={editAssetFormData.name}
                      onChange={(e) => setEditAssetFormData({ ...editAssetFormData, name: e.target.value })}
                      className="text-lg font-semibold"
                      placeholder="Asset name"
                      data-testid="edit-asset-name"
                    />
                  ) : (
                    <DialogTitle className="flex items-center gap-3">
                      <Package className="w-5 h-5" />
                      {selectedAsset.name}
                    </DialogTitle>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {!isEditingAsset ? (
                    (hasPermission('edit_assets') || hasPermission('manage_assets')) && (
                      <Button variant="outline" size="sm" onClick={startEditingAsset} data-testid="edit-asset-detail-btn">
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={cancelEditingAsset}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveAssetEdit} disabled={savingAssetEdit} data-testid="save-asset-detail-btn">
                        {savingAssetEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Description</Label>
                  {isEditingAsset ? (
                    <Textarea
                      value={editAssetFormData.description}
                      onChange={(e) => setEditAssetFormData({ ...editAssetFormData, description: e.target.value })}
                      placeholder="Asset description..."
                      rows={3}
                      data-testid="edit-asset-description"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {selectedAsset.description || 'No description provided'}
                    </p>
                  )}
                </div>
                
                {/* Asset Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Asset ID</Label>
                    <p className="font-mono text-sm">{selectedAsset.id?.substring(0, 8)}...</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                      <User className="w-3 h-3 inline mr-1" />
                      Created By
                    </Label>
                    <p className="font-medium text-sm">{selectedAsset.created_by_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Created
                    </Label>
                    <p className="font-medium text-sm">
                      {selectedAsset.created_at ? format(new Date(selectedAsset.created_at), 'MMM d, yyyy HH:mm') : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Children Count</Label>
                    <p className="font-medium text-sm">{selectedAsset.children_count || selectedAsset.children?.length || 0}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Documents Linked</Label>
                    <p className="font-medium text-sm">{selectedAsset.linked_documents?.length || 0}</p>
                  </div>
                  {selectedAsset.updated_at && (
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Last Updated</Label>
                      <p className="font-medium text-sm">{format(new Date(selectedAsset.updated_at), 'MMM d, yyyy HH:mm')}</p>
                    </div>
                  )}
                </div>
                
                {/* Parent Asset */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <FolderTree className="w-3 h-3 inline mr-1" />
                    Parent Asset
                  </Label>
                  {isEditingAsset ? (
                    <Select
                      value={editAssetFormData.parent_id || "none"}
                      onValueChange={(value) => setEditAssetFormData({ ...editAssetFormData, parent_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="edit-asset-parent">
                        <SelectValue placeholder="No parent (root)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No parent (root)</SelectItem>
                        {allAssets.filter(a => a.id !== selectedAsset.id).map((asset) => (
                          <SelectItem key={asset.id} value={asset.id}>
                            {asset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : selectedAsset.parent_name ? (
                    <Badge variant="secondary">{selectedAsset.parent_name}</Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">Root asset (no parent)</p>
                  )}
                </div>
                
                {/* Children */}
                {!isEditingAsset && selectedAsset.children?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                      <FolderTree className="w-3 h-3 inline mr-1" />
                      Child Assets
                    </Label>
                    <div className="space-y-2">
                      {selectedAsset.children.map(child => (
                        <div key={child.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{child.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Linked Projects */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <Link className="w-3 h-3 inline mr-1" />
                    Linked Projects
                  </Label>
                  {isEditingAsset ? (
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {projects.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No projects available</p>
                      ) : (
                        <div className="space-y-2">
                          {projects.map(project => (
                            <div 
                              key={project.id} 
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                editAssetFormData.project_ids.includes(project.id) 
                                  ? 'bg-primary/10 border border-primary/30' 
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleEditAssetProjectSelection(project.id)}
                            >
                              <Checkbox 
                                checked={editAssetFormData.project_ids.includes(project.id)}
                                onChange={() => {}}
                              />
                              <FolderTree className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm flex-1">{project.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : selectedAsset.project_names?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedAsset.project_names.map((name, i) => (
                        <Badge key={i} variant="secondary">{name}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No projects linked</p>
                  )}
                </div>
                
                {/* Linked Documents */}
                {!isEditingAsset && (
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                      <FileText className="w-3 h-3 inline mr-1" />
                      Linked Documents
                    </Label>
                    {selectedAsset.linked_documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedAsset.linked_documents.map(doc => (
                          <div 
                            key={doc.id} 
                            className="flex items-center gap-2 p-2 bg-muted/50 rounded hover:bg-muted cursor-pointer transition-colors"
                            onClick={() => fetchDocumentDetail(doc.id)}
                          >
                            <FileText className="w-4 h-4 text-blue-500" />
                            {doc.document_number && (
                              <span className="text-xs font-mono text-muted-foreground bg-background px-1.5 py-0.5 rounded">
                                {doc.document_number}
                              </span>
                            )}
                            <span className="text-sm flex-1 hover:text-primary">{doc.name}</span>
                            <Badge variant="secondary" className="text-xs">{doc.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No documents linked</p>
                    )}
                  </div>
                )}
                
                {/* Tags */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <Tag className="w-3 h-3 inline mr-1" />
                    Tags
                  </Label>
                  {isEditingAsset ? (
                    <div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {editAssetFormData.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <X 
                              className="w-3 h-3 cursor-pointer hover:text-destructive" 
                              onClick={() => removeEditAssetTag(tag)}
                            />
                          </Badge>
                        ))}
                      </div>
                      <Input
                        placeholder="Type and press Enter to add tags..."
                        onKeyDown={handleEditAssetTagKeyDown}
                        className="h-8"
                        data-testid="edit-asset-tags-input"
                      />
                    </div>
                  ) : selectedAsset.tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedAsset.tags.map((tag, i) => (
                        <Badge key={i} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tags</p>
                  )}
                </div>
                
                {/* Custom Metadata */}
                {!isEditingAsset && selectedAsset.metadata && Object.keys(selectedAsset.metadata).length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Custom Metadata</Label>
                    <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg">
                      {Object.entries(selectedAsset.metadata).map(([key, value]) => (
                        <div key={key} className="text-sm">
                          <span className="text-muted-foreground capitalize">{key}:</span>{' '}
                          <span className="font-medium">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload File Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div 
              className="upload-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('dragover');
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('dragover');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('dragover');
                if (e.dataTransfer.files?.[0]) {
                  setUploadFile(e.dataTransfer.files[0]);
                }
              }}
            >
              <input
                type="file"
                id="asset-file-upload"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
              <label htmlFor="asset-file-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {uploadFile ? uploadFile.name : 'Drag & drop or click to select'}
                </p>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!uploadFile || uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Detail Modal */}
      <Dialog open={showDocumentDetailModal} onOpenChange={(open) => {
        if (!open) {
          setIsEditingDocument(false);
          cancelEditingDocument();
          if (previewBlobUrl) {
            URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl(null);
          }
        }
        setShowDocumentDetailModal(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedDocument && (
            <>
              <DialogHeader className="flex flex-row items-start justify-between">
                <div className="flex-1">
                  {isEditingDocument ? (
                    <Input
                      value={editDocFormData.name}
                      onChange={(e) => setEditDocFormData({ ...editDocFormData, name: e.target.value })}
                      className="text-lg font-semibold"
                      placeholder="Document name"
                      data-testid="edit-doc-name"
                    />
                  ) : (
                    <DialogTitle className="flex items-center gap-3">
                      {getFileIcon(selectedDocument.file_name)}
                      <div className="flex flex-col">
                        <span>{selectedDocument.name}</span>
                        {selectedDocument.document_number && (
                          <span className="text-xs font-mono text-muted-foreground font-normal">
                            {selectedDocument.document_number}
                          </span>
                        )}
                      </div>
                    </DialogTitle>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {!isEditingDocument ? (
                    (hasPermission('edit_documents') || hasPermission('manage_documents')) && (
                      <Button variant="outline" size="sm" onClick={startEditingDocument} data-testid="edit-doc-detail-btn">
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={cancelEditingDocument}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveDocumentEdit} disabled={savingDocEdit} data-testid="save-doc-detail-btn">
                        {savingDocEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Description</Label>
                  {isEditingDocument ? (
                    <Textarea
                      value={editDocFormData.description}
                      onChange={(e) => setEditDocFormData({ ...editDocFormData, description: e.target.value })}
                      placeholder="Document description..."
                      rows={3}
                      data-testid="edit-doc-description"
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {selectedDocument.description || 'No description provided'}
                    </p>
                  )}
                </div>
                
                {/* File Preview Section */}
                {!isEditingDocument && (
                  <>
                    {previewLoading ? (
                      <div className="flex items-center justify-center h-48 bg-muted/30 rounded-lg">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : previewInfo?.has_file ? (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-muted/50 px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getFileIcon(previewInfo.file_name)}
                            <div>
                              <p className="text-sm font-medium">{previewInfo.file_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {previewInfo.file_size ? `${(previewInfo.file_size / 1024).toFixed(1)} KB` : 'Unknown size'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openFullPreview(selectedDocument)}>
                              <Maximize2 className="w-4 h-4 mr-1" />
                              Full Preview
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDocumentDownload(selectedDocument)}>
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                        
                        {/* Inline Preview */}
                        <div className="bg-background">
                          {previewInfo.preview_type === 'image' && (
                            <div className="flex items-center justify-center p-4 max-h-64 overflow-hidden">
                              {previewBlobUrl ? (
                                <img
                                  src={previewBlobUrl}
                                  alt={previewInfo.file_name}
                                  className="max-w-full max-h-56 object-contain rounded"
                                />
                              ) : (
                                <div className="flex items-center justify-center h-32 text-muted-foreground">
                                  <Loader2 className="w-6 h-6 animate-spin" />
                                </div>
                              )}
                            </div>
                          )}
                          {previewInfo.preview_type === 'pdf' && (
                            <div className="h-64">
                              {previewBlobUrl ? (
                                <iframe
                                  src={previewBlobUrl}
                                  className="w-full h-full border-0"
                                  title="PDF Preview"
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full text-muted-foreground">
                                  <Loader2 className="w-6 h-6 animate-spin" />
                                </div>
                              )}
                            </div>
                          )}
                          {previewInfo.preview_type === 'office' && (
                            <div className="p-4 text-center">
                              <div className="flex flex-col items-center gap-3 py-4">
                                {getFileIcon(previewInfo.file_name)}
                                <p className="text-sm text-muted-foreground">
                                  Office document - Click &quot;Full Preview&quot; to view in Microsoft Office Online
                                </p>
                              </div>
                            </div>
                          )}
                          {previewInfo.preview_type === 'other' && (
                            <div className="p-4 text-center">
                              <div className="flex flex-col items-center gap-3 py-4">
                                <File className="w-12 h-12 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">
                                  Preview not available for this file type
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="border border-dashed rounded-lg p-6 text-center">
                        <File className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">No file attached</p>
                      </div>
                    )}
                  </>
                )}
                
                {/* Document Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Document Number</Label>
                    {isEditingDocument ? (
                      <Input
                        value={editDocFormData.document_number}
                        onChange={(e) => setEditDocFormData({ ...editDocFormData, document_number: e.target.value })}
                        placeholder="DOC-001"
                        className="h-8"
                        data-testid="edit-doc-number"
                      />
                    ) : (
                      <p className="font-mono font-medium">{selectedDocument.document_number || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Document Type</Label>
                    {isEditingDocument ? (
                      <Select
                        value={editDocFormData.document_type_id || "none"}
                        onValueChange={(value) => setEditDocFormData({ ...editDocFormData, document_type_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-doc-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {documentTypes.map((dt) => (
                            <SelectItem key={dt.id} value={dt.id}>
                              {dt.code} - {dt.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.document_type 
                          ? `${selectedDocument.document_type.code} - ${selectedDocument.document_type.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Revision Code</Label>
                    {isEditingDocument ? (
                      <Select
                        value={editDocFormData.revision_code_id || "none"}
                        onValueChange={(value) => setEditDocFormData({ ...editDocFormData, revision_code_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger className="h-8" data-testid="edit-revision-code">
                          <SelectValue placeholder="Select revision" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {revisionCodes.map((rc) => (
                            <SelectItem key={rc.id} value={rc.id}>
                              {rc.code} - {rc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium">
                        {selectedDocument.revision_code 
                          ? `${selectedDocument.revision_code.code} - ${selectedDocument.revision_code.name}`
                          : '-'}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Version</Label>
                    <p className="font-medium">v{selectedDocument.version}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                      <User className="w-3 h-3 inline mr-1" />
                      Creator
                    </Label>
                    <p className="font-medium">{selectedDocument.created_by_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Created
                    </Label>
                    <p className="font-medium">{format(new Date(selectedDocument.created_at), 'MMM d, yyyy HH:mm')}</p>
                  </div>
                </div>
                
                {/* Status */}
                <div className="flex items-center gap-4">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Status</Label>
                  {getStatusBadge(selectedDocument.status)}
                </div>
                
                {/* Project */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <FolderTree className="w-3 h-3 inline mr-1" />
                    Project
                  </Label>
                  {isEditingDocument ? (
                    <Select
                      value={editDocFormData.project_id || "none"}
                      onValueChange={(value) => setEditDocFormData({ ...editDocFormData, project_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="edit-project">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : selectedDocument.project_name ? (
                    <Badge variant="secondary">{selectedDocument.project_name}</Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">No project assigned</p>
                  )}
                </div>
                
                {/* Linked Assets */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <Link className="w-3 h-3 inline mr-1" />
                    Linked Assets
                  </Label>
                  {isEditingDocument ? (
                    <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                      {allAssets.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No assets available</p>
                      ) : (
                        <div className="space-y-2">
                          {allAssets.map(asset => (
                            <div 
                              key={asset.id} 
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                editDocFormData.asset_ids.includes(asset.id) 
                                  ? 'bg-primary/10 border border-primary/30' 
                                  : 'bg-muted/30 hover:bg-muted/50'
                              }`}
                              onClick={() => toggleEditDocAssetSelection(asset.id)}
                            >
                              <Checkbox 
                                checked={editDocFormData.asset_ids.includes(asset.id)}
                                onChange={() => {}}
                              />
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm flex-1">{asset.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : selectedDocument.linked_assets?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedDocument.linked_assets.map(asset => (
                        <div key={asset.id} className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{asset.name}</span>
                          {asset.asset_type && (
                            <Badge variant="secondary" className="text-xs">{asset.asset_type}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No linked assets</p>
                  )}
                </div>
                
                {/* Tags */}
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                    <Tag className="w-3 h-3 inline mr-1" />
                    Tags
                  </Label>
                  {isEditingDocument ? (
                    <div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {editDocFormData.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="flex items-center gap-1">
                            {tag}
                            <X 
                              className="w-3 h-3 cursor-pointer hover:text-destructive" 
                              onClick={() => removeEditDocTag(tag)}
                            />
                          </Badge>
                        ))}
                      </div>
                      <Input
                        placeholder="Type and press Enter to add tags..."
                        onKeyDown={handleEditDocTagKeyDown}
                        className="h-8"
                        data-testid="edit-tags-input"
                      />
                    </div>
                  ) : selectedDocument.tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedDocument.tags.map((tag, i) => (
                        <Badge key={i} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tags</p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDocumentDetailModal(false);
                    navigate('/documents');
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in Documents
                </Button>
                <Button onClick={() => setShowDocumentDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assets;
